import React, { useMemo, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Form, Popconfirm, notification } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import DF from 'DisplayFramework';
import { indexBy } from 'utils/utils2';
import AddButton from 'components/AddButtonSmall';
import SaveButton from 'components/SaveButtonAction';
import DataGrid from 'components/DataGrid/DataGrid';
import ParListSelect from 'components/ParListSelect';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { useFormList } from 'hooks/useFormList';
import {
  getAssetCatalogCriteria,
  addAssetCatalogCriteria,
  removeAssetCatalogCriteria,
  ParAssetCatalogCriteriaResponse,
  AssetCatalogResponse,
} from 'services/assetconfig';
import DeleteButton from 'components/DeleteButton';
import { isNil } from 'lodash';

const NEW_ASSET_CATALOG_CRITERIA_ID = -1;

type AssetCatalogCriteriaTableProps = {
  asset: AssetCatalogResponse;
  isModal?: string;
};

const AssetCatalogCriteriaTable = (props: AssetCatalogCriteriaTableProps) => {
  const { catalogid } = props.asset;
  const { isModal } = props;
  const [form] = Form.useForm();
  const fieldName = 'criteria';

  const { add } = useFormList(form, fieldName, {
    idKey: 'criid',
    addAsFirst: true,
  });
  const [loadingBtns, setLoadingBtns] = useState<boolean>(false);
  const { data, refetch, isFetching } = useQuery(
    ['getAssetCatalogCriteria', { catalogid }],
    () => getAssetCatalogCriteria({ catalogid }).then((res) => res.list || []),
    { refetchOnMount: false, enabled: catalogid !== undefined }
  );

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        [fieldName]: data,
      });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [form, data]);

  const addRow = () => {
    const row: ParAssetCatalogCriteriaResponse = {
      criid: NEW_ASSET_CATALOG_CRITERIA_ID,
      catalogid: '' as any,
      critcode: '',
      critcodelabel: '',
      critvalue: '',
      critvaluelabel: '',
    };
    add(row);
  };

  const onSave = async (record: ParAssetCatalogCriteriaResponse) => {
    setLoadingBtns(true);
    const steps: ParAssetCatalogCriteriaResponse[] =
      form.getFieldValue(fieldName);
    const index = indexBy(steps, 'criid')[record.criid];
    const currentValue = steps[index];
    await addAssetCatalogCriteria({
      req: {
        ...currentValue,
        criid: null as any,
        catalogid,
      },
      catalogid: catalogid as any,
    })
      .then((response) => {
        if (response.code === '200') {
          notification.success({
            message: response.message,
          });
          refetch();

          setLoadingBtns(false);
        }
      })
      .catch(() => {
        setLoadingBtns(false);
      });
  };

  const columns = useMemo<DataGridColDef<ParAssetCatalogCriteriaResponse>[]>(
    () => [
      // {
      //   dataIndex: 'criid',
      //   width: 100,
      //   render: (_value, _record, index) => (
      //     <Form.Item
      //       name={[
      //         fieldName,
      //         indexBy(form.getFieldValue(fieldName), 'criid')[_record.criid],
      //         'criid',
      //       ]}
      //     >
      //       {_value}
      //     </Form.Item>
      //   ),
      // },
      {
        dataIndex: 'critcode',
        render: (_value, _record, index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'criid')[_record.criid],
              'critcode',
            ]}
          >
            <ParListSelect parcode="ASSCRITER" />
          </Form.Item>
        ),
        width: 180,
      },
      {
        dataIndex: 'critvalue',
        render: (_value, _record, index) => (
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => (
              <Form.Item
                name={[
                  fieldName,
                  indexBy(getFieldValue(fieldName), 'criid')[_record.criid],
                  'critvalue',
                ]}
                rules={[{ required: true }]}
              >
                <ParListSelect
                  parcode={getFieldValue([fieldName, index, 'critcode'])}
                />
              </Form.Item>
            )}
          </Form.Item>
        ),
        width: 160,
      },
      {
        key: 'actions',
        render: (_, _record) => (
          <>
            {_record.criid === NEW_ASSET_CATALOG_CRITERIA_ID && (
              <SaveButton
                onClick={(ev) => {
                  ev.preventDefault();
                  onSave(_record);
                }}
                size="small"
                comcode="save"
                loading={loadingBtns||isFetching}
              />
            )}
            <Popconfirm
              title="Sure to delete?"
              onConfirm={() => {
                removeAssetCatalogCriteria({
                  catalogid,
                  criid: _record.criid,
                }).then((res) => {
                  notification.success({ message: res.message });
                  refetch();
                });
              }}
            >
              <DeleteButton onlyIcon />
            </Popconfirm>
          </>
        ),
        width: 70,
        fixed: 'right',
      },
    ],
    [catalogid, form, onSave, refetch, loadingBtns]
  );

  return (
    <DF.Provider pagcode="assetCriteria">
      <Form form={form} initialValues={INITIAL_VALUES}>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const criteria: ParAssetCatalogCriteriaResponse[] =
              getFieldValue(fieldName);

            const isNewItemInTable =
              criteria?.find(
                ({ criid }) => criid === NEW_ASSET_CATALOG_CRITERIA_ID
              ) !== undefined;

            return (
              <DataGrid
                rowKey="criid"
                columns={columns}
                titleComcode="Criteria"
                dataSource={criteria}
                hideFilters
                extraActions={
                  <AddButton
                    icon={<PlusOutlined />}
                    type="primary"
                    onClick={addRow}
                    comcode="add"
                    disabled={isNewItemInTable}
                    hidden={isNil(catalogid) || !!isModal}
                  />
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};

const INITIAL_VALUES = {
  criteria: [],
};

export default AssetCatalogCriteriaTable;
